import React from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router-dom';

import { ConfirmationDialog } from '../components/confirmation-dialog/confirmation-dialog';
import { useDialog } from '../hooks/use-dialog';
import { useAppContext } from '../state/app-state';

/**
 * When pageHasUnsavedChanges is true, blocks in-app navigation and shows a
 * confirmation dialog so the user can choose to leave or stay. Must be rendered
 * inside a React Router data router (RouterProvider from createBrowserRouter).
 */
export function UnsavedChangesBlocker() {
  const { pageHasUnsavedChanges } = useAppContext();
  const blocker = useBlocker(pageHasUnsavedChanges);
  const { t } = useTranslation();
  const confirmDialog = useDialog();
  const hasShownForThisBlockRef = React.useRef(false);

  React.useEffect(() => {
    if (blocker.state === 'blocked') {
      if (!hasShownForThisBlockRef.current) {
        hasShownForThisBlockRef.current = true;
        confirmDialog.show();
      }
    } else {
      hasShownForThisBlockRef.current = false;
    }
  }, [blocker.state, confirmDialog]);

  const handleConfirm = React.useCallback(() => {
    confirmDialog.hide();
    blocker.proceed?.();
  }, [blocker, confirmDialog]);

  const handleCancel = React.useCallback(() => {
    confirmDialog.hide();
    blocker.reset?.();
  }, [blocker, confirmDialog]);

  return (
    <>
      {confirmDialog.render(
        <ConfirmationDialog
          cancelText={t('cancel', { ns: 'common' })}
          confirmationText={t('continue', { ns: 'common' })}
          title={`⚠️ ${t('toaster.title.warning', { ns: 'common' })}`}
          onCancel={handleCancel}
          onConfirm={() => Promise.resolve(handleConfirm())}
        >
          {t('confirmExit', { ns: 'common' })}
        </ConfirmationDialog>,
      )}
    </>
  );
}
